import { MIDDLE_STYLE } from '@constants/responsive';
import { GLOBAL_THEME_COLOR } from '@constants/theme';
import { Avatar, Col, Row, Space } from 'antd';
import React, { useState } from 'react';
import { ReservationStatusBadge, ReservationTimeRender, SquareIconButton } from '..';
import { Reservation, ReservationStatus } from '@core/models';
import { stringToColour } from '@utils/string.util';
import { getReservationFullName } from '@utils/booking.util';
import {
  CheckOutlined,
  DeleteOutlined,
  DoubleRightOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { bookingApiService } from '@services/service-register';
import { useAppDispatch } from '@core/configureStore';
import { fetchReservations } from '@core/features/booking/thunks/fetchReservations.thunk';
import { openModal } from '@core/features';

type Props = {
  reservation: Reservation<any>;
  renderExtraParams?: (reservation: Reservation<any>) => React.ReactNode[] | React.ReactNode;
};

const ReservationListCard = ({ reservation, renderExtraParams }: Props) => {
  const dispatch = useAppDispatch();
  const [actionLoading, setActionLoading] = useState<ReservationStatus | undefined>(undefined);

  const handleUpdateReservationStatus = async (
    reservation: Reservation<any>,
    status: ReservationStatus
  ) => {
    switch (status) {
      case ReservationStatus.Finished:
        setActionLoading(ReservationStatus.Finished);
        await bookingApiService.finishReservation(reservation.id);
        setActionLoading(undefined);
        break;
      case ReservationStatus.Skipped:
        setActionLoading(ReservationStatus.Skipped);
        await bookingApiService.skipReservation(reservation.id);
        setActionLoading(undefined);
        break;
      case ReservationStatus.InProgress:
        setActionLoading(ReservationStatus.InProgress);
        await bookingApiService.serveReservation(reservation.id);
        setActionLoading(undefined);
        break;
      default:
        break;
    }
    await dispatch(fetchReservations());
  };

  const handleDeleteReservation = async (reservation: Reservation<any>) => {
    try {
      await bookingApiService.deleteReservation(reservation.id);
      await dispatch(fetchReservations());
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditReservation = (reservation: Reservation<any>) => {
    dispatch(
      openModal({
        modalName: 'reservationDetailModal',
        extraParams: {
          existingReservation: reservation,
        },
      })
    );
  };

  return (
    <Row
      gutter={20}
      style={{
        ...MIDDLE_STYLE,
        width: '100%',
        padding: '10px 15px',
        borderTop: `0.5px solid ${GLOBAL_THEME_COLOR.$border_color}`,
      }}>
      <Col span={3}>
        <ReservationTimeRender reservation={reservation} />
      </Col>
      <Col span={2}>
        <Avatar style={{ backgroundColor: stringToColour(getReservationFullName(reservation)) }}>
          {reservation.firstName?.at(0)}
        </Avatar>
      </Col>
      <Col span={4}>
        <span
          style={{
            fontWeight: '500',
          }}>
          {getReservationFullName(reservation)}
        </span>
      </Col>
      <Col span={3}>
        <ReservationStatusBadge reservation={reservation} />
      </Col>
      <Col span={2}>{reservation.numberOfPeople}</Col>
      <Col span={5}>{renderExtraParams && renderExtraParams(reservation)}</Col>
      <Col span={5} style={{ ...MIDDLE_STYLE, justifyContent: 'flex-end' }}>
        {reservation.status !== ReservationStatus.Finished &&
          reservation.status !== ReservationStatus.Skipped && (
            <Space>
              <SquareIconButton
                title="Delete"
                buttonProps={{
                  onClick: () => handleDeleteReservation(reservation),
                }}
                icon={<DeleteOutlined />}
              />
              <SquareIconButton
                title="Skip Customer"
                buttonProps={{
                  loading: actionLoading === ReservationStatus.Skipped,
                  onClick: () =>
                    handleUpdateReservationStatus(reservation, ReservationStatus.Skipped),
                }}
                icon={<DoubleRightOutlined />}
              />
              <SquareIconButton
                title="Edit"
                buttonProps={{
                  onClick: () => handleEditReservation(reservation),
                }}
                icon={<EditOutlined />}
              />
              <SquareIconButton
                title="Finish"
                buttonProps={{
                  type: 'primary',
                  loading: actionLoading === ReservationStatus.Finished,
                  onClick: () =>
                    handleUpdateReservationStatus(reservation, ReservationStatus.Finished),
                }}
                icon={<CheckOutlined />}
              />
            </Space>
          )}
      </Col>
    </Row>
  );
};

export default ReservationListCard;
